import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  Table,
  Row,
  Cell,
  Checkbox,
  Textfield,
  Notification,
} from '@workboard/wobo-ui';
import InfoOutlined from '@material-ui/icons/InfoOutlined';
import {
  MemberRequest,
  MemberRequestResponse,
  MemberRequestRow,
} from '../../../../types/teams-management';
import { ApproveMenu } from './approve-menu';

export type MemberRequestContainerProps = {
  teamId: number | undefined;
  userId: number;
};

const MemberRequestMainContainer = styled('div')`
  width: 100%;
  height: 100%;
  padding: 20px;
`;
const BulkActionContainer = styled('div')`
  height: 50px;
  position: relative;
  background: #eef8fd;

  h3 {
    position: absolute;
    top: 10px;
    left: 5px;
  }
`;
const BulkActionButtons = styled('div')`
  position: absolute;
  height: 40px;
  width: 320px;
  top: 5px;
  right: 10px;
`;
const FilterContainer = styled('div')`
  height: 50px;
  position: relative;

  h3 {
    position: absolute;
    top: 10px;
    left: 5px;
  }
`;
const FilterDiv = styled('div')`
  position: absolute;
  height: 40px;
  width: 320px;
  top: 5px;
  right: 10px;
`;
const NoRequestsContainer = styled('div')`
  width: 350px;
  height: 200px;
  margin: auto;
`;
const NoRequestsMessage = styled('span')`
  padding-left: 5px;
`;

export const MemberRequestContainer = ({
  teamId,
  userId,
}: MemberRequestContainerProps): JSX.Element => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [memberRequests, setMemberRequests] = useState([] as MemberRequest[]);
  const [checkedMemberRequests, setCheckedMemberRequests] = useState(
    [] as number[],
  );
  const [allChecked, setAllChecked] = useState(false);
  const [perPage, setPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filterValue, setFilterValue] = useState('');
  const [alert, setAlert] = useState({
    show: false,
    type: 'success',
    message: '',
  });

  const getMemberRequests = useCallback(() => {
    setIsLoading(true);
    axios({
      method: 'get',
      url: `${window.wbbaseUrl}/teams/request`,
      params: {
        perPage,
        page,
        teamId,
        userId,
        search: filterValue.length > 1 ? filterValue : undefined,
        markViewed: true,
      },
    })
      .then((res) => {
        if (res.data.message.type !== 'error') {
          const rawMemberRequests = res.data.data;
          const mappedMemberRequests = rawMemberRequests.map(
            (request: MemberRequest): MemberRequestRow => ({
              id: request.id,
              requestorName: request.requestor_name,
              requestorTitle: request.requestor_title,
              requestMessage: request.request_message,
              teamName: request.team_name,
            }),
          );
          setMemberRequests(mappedMemberRequests);
          setTotalCount(res.data.pagination.totalRecords);
        } else {
          setAlert({
            show: true,
            type: 'error',
            message: t('Oops, something went wrong. Please try again later.'),
          });
          setMemberRequests([]);
        }
      })
      .catch(() => {
        setAlert({
          show: true,
          type: 'error',
          message: t('Oops, something went wrong. Please try again later.'),
        });
      })
      .finally(() => setIsLoading(false));
  }, [page, perPage, filterValue, teamId, userId]);

  const handleMemberRequestResponse = useCallback(
    async (requestsToApprove: MemberRequestResponse[]) => {
      axios({
        method: 'post',
        url: `${window.wbbaseUrl}/teams/request`,
        data: requestsToApprove,
      })
        .then((res) => {
          if (res.data.message.type !== 'error') {
            setAlert({
              show: true,
              type: 'success',
              message: t(
                checkedMemberRequests.length > 1
                  ? '{{count}} member requests {{status}}'
                  : '{{count}} member request {{status}}',
                {
                  count: checkedMemberRequests.length || 1,
                  status:
                    requestsToApprove[0].status === 'approved'
                      ? t('approved')
                      : t('rejected'),
                },
              ),
            });
            setCheckedMemberRequests([]);
            setAllChecked(false);
            getMemberRequests();
          } else {
            setAlert({
              show: true,
              type: 'error',
              message: t('Oops, something went wrong. Please try again later.'),
            });
          }
        })
        .catch(() => {
          setAlert({
            show: true,
            type: 'error',
            message: t('Oops, something went wrong. Please try again later.'),
          });
        })
        .finally(() => setIsLoading(false));
    },
    [checkedMemberRequests.length, getMemberRequests, t],
  );

  useEffect(() => {
    getMemberRequests();
  }, [getMemberRequests, page, perPage]);

  const handleTableChange = (pageData: {
    page: number;
    rowsPerPage: number;
  }): void => {
    if (pageData.page + 1 !== page && pageData.page >= 0) {
      setPage(pageData.page + 1);
    }
    if (pageData.rowsPerPage !== perPage) {
      setPerPage(pageData.rowsPerPage);
    }
  };

  const handleFilterChange = (e: { target: { value: string } }): void => {
    const newFilterValue = e.target.value;
    setFilterValue(newFilterValue);
  };

  const handleCheckAll = (): void => {
    if (allChecked) {
      setCheckedMemberRequests([]);
      setAllChecked(false);
    } else {
      setCheckedMemberRequests(memberRequests.map((request) => request.id));
      setAllChecked(true);
    }
  };

  const HeaderRow = (): JSX.Element => (
    <Row>
      <Cell style={{ background: '#fff' }} align="center">
        <Checkbox onClick={handleCheckAll} isChecked={allChecked} />
      </Cell>
      {teamId ? null : (
        <Cell style={{ background: '#fff' }}>{t('Team name')}</Cell>
      )}
      <Cell style={{ background: '#fff' }} align="left">
        {t('Name')}
      </Cell>
      <Cell style={{ background: '#fff' }} align="left">
        {t('Title')}
      </Cell>
      <Cell style={{ background: '#fff' }} align="left">
        {t('Note')}
      </Cell>
      <Cell style={{ background: '#fff' }} align="left">
        {t('Actions')}
      </Cell>
    </Row>
  );

  const TableRow = ({
    id,
    requestorName,
    requestorTitle,
    requestMessage,
    teamName,
    ...props
  }: MemberRequestRow): JSX.Element => {
    const checked = checkedMemberRequests.includes(id);
    return (
      <Row {...props} style={{ backgroundColor: checked ? '#EEF8FD' : '#FFF' }}>
        <Cell align="center">
          <Checkbox
            onClick={() => {
              if (checked) {
                setCheckedMemberRequests(
                  checkedMemberRequests.filter((requestId) => requestId !== id),
                );
                setAllChecked(false);
              } else {
                setCheckedMemberRequests([...checkedMemberRequests, id]);
                if (
                  checkedMemberRequests.length + 1 ===
                  memberRequests.length
                ) {
                  setAllChecked(true);
                }
              }
            }}
            isChecked={checked}
          />
        </Cell>
        {teamId ? null : <Cell style={{ background: '#fff' }}>{teamName}</Cell>}
        <Cell align="left">{requestorName}</Cell>
        <Cell align="left">{requestorTitle}</Cell>
        <Cell align="left">{requestMessage}</Cell>
        <Cell align="left" style={{ width: '250px' }}>
          <ApproveMenu
            memberRequestIdsToApprove={[id]}
            handleMemberRequestResponse={handleMemberRequestResponse}
          />
        </Cell>
      </Row>
    );
  };

  return (
    <MemberRequestMainContainer>
      {checkedMemberRequests.length > 0 ? (
        <BulkActionContainer>
          <h3>
            {t(
              checkedMemberRequests.length === 1
                ? '{{count}} request selected'
                : '{{count}} requests selected',
              { count: checkedMemberRequests.length },
            )}
          </h3>
          <BulkActionButtons>
            <ApproveMenu
              memberRequestIdsToApprove={checkedMemberRequests}
              handleMemberRequestResponse={handleMemberRequestResponse}
            />
          </BulkActionButtons>
        </BulkActionContainer>
      ) : (
        <FilterContainer>
          <h3>
            {t(
              totalCount === 1
                ? '{{count}} member request'
                : '{{count}} member requests',
              { count: totalCount },
            )}
          </h3>
          <FilterDiv>
            <Textfield
              onChange={handleFilterChange}
              placeholder={t('Filter')}
              type="text"
              value={filterValue}
            />
          </FilterDiv>
        </FilterContainer>
      )}
      {memberRequests.length === 0 ? (
        <NoRequestsContainer>
          <InfoOutlined />
          <NoRequestsMessage>
            {t('There are no member requests pending')}
          </NoRequestsMessage>
        </NoRequestsContainer>
      ) : (
        <Table
          data={memberRequests}
          data-testid="wbui-table-team-member-requests"
          header={HeaderRow}
          row={TableRow}
          rowsPerPage={perPage}
          rowsPerPageValues={{
            isAll: false,
            pageNumbers: [10, 25, 50],
          }}
          totalDataCount={totalCount > perPage ? totalCount : undefined}
          isLoading={isLoading}
          onChange={handleTableChange}
        />
      )}
      <Notification
        open={alert.show}
        severity={alert.type}
        message={alert.message}
        onClose={() => setAlert({ show: false, type: 'success', message: '' })}
      />
    </MemberRequestMainContainer>
  );
};

export default MemberRequestContainer;
